// src/errors.ts
var HTTP_ERROR_BODY_MAX_BYTES = 4 * 1024;
var XoneHttpError = class _XoneHttpError extends Error {
  code;
  status;
  body;
  constructor(code, status, body) {
    const truncated = truncateBody(body);
    super(buildMessage(code, status, truncated));
    this.name = "XoneHttpError";
    this.code = code;
    this.status = status;
    this.body = truncated;
    Object.setPrototypeOf(this, _XoneHttpError.prototype);
  }
};
function buildMessage(code, status, body) {
  if (code === -1) {
    return `XoneHttpError(-1): timeout o red caida${body ? ` - ${body}` : ""}`;
  }
  if (code === 0) {
    return `XoneHttpError(0): error desconocido${body ? ` - ${body}` : ""}`;
  }
  const head = `XoneHttpError(${code}, status=${status})`;
  return body ? `${head}: ${body}` : head;
}
function truncateBody(body) {
  if (!body) return "";
  if (body.length <= HTTP_ERROR_BODY_MAX_BYTES) return body;
  return body.slice(0, HTTP_ERROR_BODY_MAX_BYTES);
}

// src/multipart.ts
function buildFormData(data) {
  const form = new FormData();
  for (const [rawKey, rawValue] of Object.entries(data)) {
    if (rawValue === null || rawValue === void 0) continue;
    appendField(form, rawKey, rawValue);
  }
  return form;
}
function appendField(form, key, value) {
  if (Array.isArray(value)) {
    for (const item of value) {
      if (item === null || item === void 0) continue;
      appendField(form, key, item);
    }
    return;
  }
  if (value instanceof Blob) {
    form.append(key, value);
    return;
  }
  if (value instanceof ArrayBuffer) {
    form.append(key, new Blob([value]));
    return;
  }
  if (ArrayBuffer.isView(value)) {
    const view = value;
    const copy = new Uint8Array(view.byteLength);
    copy.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
    form.append(key, new Blob([copy.buffer]));
    return;
  }
  if (typeof value === "string") {
    form.append(key, value);
    return;
  }
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    form.append(key, String(value));
    return;
  }
  if (typeof value === "object") {
    form.append(key, JSON.stringify(value));
    return;
  }
  form.append(key, String(value));
}
function isBinaryBody(body) {
  return body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body);
}

// src/refresh.ts
var TokenRefresher = class {
  constructor(hook) {
    this.hook = hook;
  }
  hook;
  inFlight = null;
  /**
   * Ejecuta el hook (compartiendo la misma promesa para llamadas
   * concurrentes). Resuelve cuando el hook ha terminado correctamente.
   */
  async refresh() {
    if (this.inFlight) {
      return this.inFlight;
    }
    const promise = (async () => {
      try {
        await this.hook();
      } finally {
        this.inFlight = null;
      }
    })();
    this.inFlight = promise;
    return promise;
  }
};

// src/client.ts
var DEFAULT_TIMEOUT_MS = 3e4;
var TOKEN_MACRO_DEFAULT = "TOKEN";
var HttpClient = class {
  constructor(config) {
    this.config = config;
    this.globalHeaders = { ...config.globalHeaders ?? {} };
    this.defaultTimeoutMs = config.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.fetchImpl = config.fetchImpl ?? ((...args) => {
      const fn = globalThis.fetch;
      if (!fn) {
        throw new Error("fetch no disponible en el runtime actual");
      }
      return fn(...args);
    });
    this.tokenMacroName = config.tokenMacroName ?? TOKEN_MACRO_DEFAULT;
    this.refresher = config.onTokenExpired ? new TokenRefresher(config.onTokenExpired) : null;
  }
  config;
  globalHeaders;
  defaultTimeoutMs;
  fetchImpl;
  tokenMacroName;
  refresher;
  /**
   * Ejecuta el request, con auto-refresh 401 si esta configurado.
   * Lanza `XoneHttpError` ante cualquier error.
   */
  async send(method, url, req) {
    const normalized = this.normalize(method, url, req);
    try {
      return await this.doFetch(normalized);
    } catch (err) {
      if (err instanceof XoneHttpError && err.status === 401 && this.refresher) {
        try {
          await this.refresher.refresh();
        } catch {
          throw err;
        }
        return await this.doFetch(normalized);
      }
      throw err;
    }
  }
  /**
   * Descarga binaria. Devuelve `{ blob, contentType, suggestedName, headers }`.
   * Persistencia en OPFS la realiza el caller (plan 07-storage).
   */
  async download(url, headers) {
    const normalized = this.normalize("GET", url, headers ? { headers } : null);
    const controller = new AbortController();
    const timeoutMs = normalized.timeoutMs;
    const timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await this.fetchImpl(normalized.url, {
        method: "GET",
        headers: this.materializeHeaders(normalized.headers),
        signal: controller.signal
      });
      if (!response.ok) {
        const body = await safeReadText(response);
        throw new XoneHttpError(response.status, response.status, body);
      }
      const blob = await response.blob();
      const responseHeaders = collectHeaders(response.headers);
      return {
        blob,
        contentType: responseHeaders["content-type"] ?? "",
        suggestedName: parseFilename(responseHeaders["content-disposition"] ?? ""),
        headers: responseHeaders,
        status: response.status
      };
    } catch (err) {
      throw toXoneHttpError(err);
    } finally {
      clearTimeout(timeoutHandle);
    }
  }
  // ------------------------------------------------------------------
  // Internos
  // ------------------------------------------------------------------
  async doFetch(req) {
    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), req.timeoutMs);
    try {
      const headers = this.materializeHeaders(req.headers);
      const response = await this.fetchImpl(req.url, {
        method: req.method,
        headers,
        body: req.body,
        signal: controller.signal
      });
      const data = await safeReadText(response);
      const respHeaders = collectHeaders(response.headers);
      if (!response.ok) {
        throw new XoneHttpError(response.status, response.status, data);
      }
      const contentType = respHeaders["content-type"] ?? "";
      const json = isJsonContentType(contentType) ? parseJsonSafe(data) : null;
      return Object.freeze({
        data,
        json,
        attachments: Object.freeze([]),
        status: response.status,
        headers: Object.freeze(respHeaders)
      });
    } catch (err) {
      throw toXoneHttpError(err);
    } finally {
      clearTimeout(timeoutHandle);
    }
  }
  normalize(method, url, req) {
    const request = req == null ? {} : typeof req === "string" ? { data: req } : req;
    const params = request.params;
    const finalUrl = params && Object.keys(params).length > 0 ? appendQueryString(url, params) : url;
    const headers = {
      ...this.globalHeaders,
      ...request.headers ?? {}
    };
    const { body, contentTypeOverride } = serializeBody(
      method,
      request.data,
      headers,
      request.multipart === true
    );
    if (contentTypeOverride !== void 0) {
      if (contentTypeOverride === null) {
        delete headers["Content-Type"];
        delete headers["content-type"];
      } else {
        headers["Content-Type"] = contentTypeOverride;
      }
    }
    return {
      method,
      url: finalUrl,
      headers,
      body,
      timeoutMs: request.timeout ?? this.defaultTimeoutMs
    };
  }
  /**
   * Combina headers normalizados con la macro `TOKEN` justo antes del envio.
   * Si el caller proporciono `Authorization` explicito, lo respeta.
   */
  materializeHeaders(headers) {
    const out = { ...headers };
    const hasAuth = "Authorization" in out || "authorization" in out;
    if (!hasAuth && this.config.macroReader) {
      const token = this.config.macroReader.getGlobalMacro(this.tokenMacroName);
      if (token) {
        out["Authorization"] = `Bearer ${token}`;
      }
    }
    return out;
  }
};
function appendQueryString(url, params) {
  const sep = url.includes("?") ? "&" : "?";
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) usp.append(k, v);
  const qs = usp.toString();
  return qs ? `${url}${sep}${qs}` : url;
}
function serializeBody(method, data, headers, forceMultipart) {
  if (data === void 0 || data === null) {
    return { body: void 0, contentTypeOverride: void 0 };
  }
  if (method === "GET") {
    return { body: void 0, contentTypeOverride: void 0 };
  }
  if (typeof data === "string") {
    return {
      body: data,
      contentTypeOverride: getHeader(headers, "Content-Type") ? void 0 : "text/plain; charset=utf-8"
    };
  }
  if (isBinaryBody(data)) {
    return {
      body: data,
      contentTypeOverride: data instanceof FormData ? null : void 0
    };
  }
  if (forceMultipart) {
    return {
      body: buildFormData(data),
      contentTypeOverride: null
    };
  }
  const existingType = getHeader(headers, "Content-Type");
  if (existingType && existingType.toLowerCase().includes("multipart/form-data")) {
    return {
      body: buildFormData(data),
      contentTypeOverride: null
    };
  }
  if (existingType && existingType.toLowerCase().includes("application/x-www-form-urlencoded")) {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(data)) {
      if (v === null || v === void 0) continue;
      usp.append(k, typeof v === "string" ? v : JSON.stringify(v));
    }
    return { body: usp.toString(), contentTypeOverride: void 0 };
  }
  return {
    body: JSON.stringify(data),
    contentTypeOverride: existingType ? void 0 : "application/json"
  };
}
function getHeader(headers, name) {
  const lower = name.toLowerCase();
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === lower) return v;
  }
  return void 0;
}
async function safeReadText(response) {
  try {
    return await response.text();
  } catch {
    return "";
  }
}
function collectHeaders(h) {
  const out = {};
  h.forEach((value, key) => {
    out[key.toLowerCase()] = value;
  });
  return out;
}
function isJsonContentType(ct) {
  if (!ct) return false;
  const lower = ct.toLowerCase();
  return lower.includes("application/json") || lower.includes("+json");
}
function parseJsonSafe(data) {
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}
function parseFilename(disposition) {
  if (!disposition) return "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match?.[1] ?? "";
}
function toXoneHttpError(err) {
  if (err instanceof XoneHttpError) return err;
  if (err instanceof Error) {
    if (err.name === "AbortError") {
      return new XoneHttpError(-1, 0, "timeout");
    }
    if (err.name === "TypeError" || err.message?.toLowerCase().includes("network") || err.message?.toLowerCase().includes("fetch")) {
      return new XoneHttpError(-1, 0, err.message || "network error");
    }
    return new XoneHttpError(0, 0, err.message || "unknown error");
  }
  return new XoneHttpError(0, 0, String(err));
}

// src/dual.ts
function dispatchDual(client, method, url, args) {
  const arg0 = args[0];
  const arg1 = args[1];
  const arg2 = args[2];
  const arg3 = args[3];
  const isCallbackMode = typeof arg1 === "function" && typeof arg2 === "function";
  if (isCallbackMode) {
    const onOk = arg1;
    const onErr = arg2;
    const extraHeaders = arg3;
    const request2 = buildCallbackRequest(method, arg0, extraHeaders);
    void runCallback(client, method, url, request2, onOk, onErr);
    return;
  }
  const request = arg0;
  return client.send(method, url, request ?? null);
}
function buildCallbackRequest(method, raw, extraHeaders) {
  if (raw == null) {
    return extraHeaders ? { headers: extraHeaders } : null;
  }
  if (typeof raw === "string") {
    if (method === "GET") {
      const params = parseQueryString(raw);
      const req2 = { params };
      if (extraHeaders) req2.headers = extraHeaders;
      return req2;
    }
    const req = { data: raw };
    if (extraHeaders) req.headers = extraHeaders;
    return req;
  }
  if (typeof raw === "object") {
    const base = raw;
    if (extraHeaders) {
      return { ...base, headers: { ...base.headers ?? {}, ...extraHeaders } };
    }
    return base;
  }
  return null;
}
function parseQueryString(qs) {
  const out = {};
  const trimmed = qs.startsWith("?") ? qs.slice(1) : qs;
  if (!trimmed) return out;
  const usp = new URLSearchParams(trimmed);
  usp.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}
async function runCallback(client, method, url, request, onOk, onErr) {
  try {
    const response = await client.send(method, url, request);
    onOk(response.data, [...response.attachments]);
  } catch (err) {
    const xhe = err instanceof XoneHttpError ? err : new XoneHttpError(0, 0, String(err));
    onErr(xhe.code, xhe.body);
  }
}

// src/index.ts
function create$Http(config = {}) {
  const client = new HttpClient(config);
  let currentProxy = null;
  function get(url, ...rest) {
    return dispatchDual(client, "GET", url, rest);
  }
  function post(url, ...rest) {
    return dispatchDual(client, "POST", url, rest);
  }
  function put(url, ...rest) {
    return dispatchDual(client, "PUT", url, rest);
  }
  function deleteFn(url, ...rest) {
    return dispatchDual(client, "DELETE", url, rest);
  }
  function patch(url, ...rest) {
    return dispatchDual(client, "PATCH", url, rest);
  }
  function download(url, targetPath, ...rest) {
    const arg0 = rest[0];
    const arg1 = rest[1];
    const arg2 = rest[2];
    const isCallbackMode = typeof arg0 === "function" && typeof arg1 === "function";
    if (isCallbackMode) {
      const onOk = arg0;
      const onErr = arg1;
      const headers2 = arg2;
      void runDownloadCallback(client, url, targetPath, headers2, onOk, onErr);
      return;
    }
    const headers = arg0;
    return runDownloadPromise(client, url, targetPath, headers);
  }
  function request(url, req, ...rest) {
    const method = normalizeMethod(req?.method);
    const restArgs = [stripMethod(req), ...rest];
    switch (method) {
      case "GET":
        return dispatchDual(client, "GET", url, restArgs);
      case "POST":
        return dispatchDual(client, "POST", url, restArgs);
      case "PUT":
        return dispatchDual(client, "PUT", url, restArgs);
      case "DELETE":
        return dispatchDual(client, "DELETE", url, restArgs);
      case "PATCH":
        return dispatchDual(client, "PATCH", url, restArgs);
    }
  }
  function setProxy(proxy) {
    currentProxy = proxy ?? null;
    return api;
  }
  function toString() {
    const proxyTag = currentProxy ? " proxy=set" : "";
    return `HttpRuntimeObject${proxyTag}`;
  }
  const api = {
    request,
    get,
    post,
    put,
    delete: deleteFn,
    del: deleteFn,
    patch,
    download,
    setProxy,
    toString
  };
  return api;
}
function normalizeMethod(raw) {
  if (typeof raw !== "string") return "GET";
  const upper = raw.toUpperCase();
  if (upper === "GET" || upper === "POST" || upper === "PUT" || upper === "DELETE" || upper === "PATCH") {
    return upper;
  }
  return "GET";
}
function stripMethod(req) {
  if (req == null || typeof req !== "object") return null;
  const { method: _method, ...rest } = req;
  void _method;
  return rest;
}
async function runDownloadPromise(client, url, targetPath, headers) {
  await client.download(url, headers);
  return targetPath;
}
async function runDownloadCallback(client, url, targetPath, headers, onOk, onErr) {
  try {
    await client.download(url, headers);
    onOk(targetPath);
  } catch (err) {
    const xhe = err instanceof XoneHttpError ? err : new XoneHttpError(0, 0, String(err));
    onErr(xhe.code, xhe.body);
  }
}
export {
  HTTP_ERROR_BODY_MAX_BYTES,
  HttpClient,
  TokenRefresher,
  XoneHttpError,
  buildFormData,
  create$Http
};
//# sourceMappingURL=index.js.map